import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { FaixaDoIphone } from "@/components/AvisoDoIphone";
import { createPortal } from "react-dom";
import { NomeDaMesa } from "@/lib/mesa";
import { useSearch, useLocation } from "wouter";
import { useListOrders, useUpdateOrderStatus } from "@workspace/api-client-react";
import { botSendsStatusTo, useWhatsappBot } from "@/lib/whatsapp-bot";
import { aniversarioParaMostrar, formatCurrency, formatWhatsApp, linkDoWhatsApp, orderDisplayCode, rotuloDaModalidade, telefoneDoLink, temWhatsApp } from "@/lib/utils";
import { fmtPontos } from "@/lib/loyalty";
import { brDayKey, formatBRDateTimeShort } from "@/lib/datetime";
import { dataUnicaDosItens } from "@/lib/encomendaUnica";
import { comLinhaDoTroco, encomendaPorExtenso, horarioDosTurnos, linhaDoTroco, gruposComConta, ordemNoDia, prepCountLines, trocarTroco, trocoDoPedido } from "@workspace/pricing";
import { PeriodFilter } from "@/components/admin/PeriodFilter";
import { EMPTY_PERIOD_STATE, resolvePeriod, periodFileTag, type PeriodState } from "@/lib/period-filter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Label } from "@/components/ui/label";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import { Loader2, MessageCircle, MapPin, Search, ChevronDown, Check, X, Copy, ExternalLink, Package, ShoppingBag, Clock, Printer, CheckCircle2, Plus, Pencil, Trash2, Bike, Filter, Minus, Download, Info, WifiOff, LogIn, RefreshCw } from "lucide-react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { getListOrdersQueryKey, customFetch } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { extractApiErrorMessage } from "@/lib/api-error";
import { useAuth } from "@/hooks/use-auth";
import { OpcaoAppWhatsApp } from "@/components/admin/OpcaoAppWhatsApp";
import { abreWhatsApp } from "@/lib/abreWhatsApp";
import { useThermalPrinter } from "@/hooks/use-thermal-printer";
import { formatReceiptBytes, formatReceiptHTML, orderToReceipt } from "@/lib/receipt-formatter";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { SectionErrorBoundary } from "@/components/ErrorBoundary";
import { ModalShell } from "@/components/ui/modal-shell";
import { Switch } from "@/components/ui/switch";
import { PublicLinkCard } from "@/components/PublicLinkCard";
import { EmptyState } from "@/components/ui/empty-state";
import { RegistroDoAviso, registrarAviso, tentarERegistrar } from "@/lib/avisoConexao";
import { Share2 } from "lucide-react";
import { errorTitle, TRY_AGAIN_DESCRIPTION } from "@/lib/messages";
import { birthdayProximity } from "@/lib/birthday";
import { formatarPeso } from "@workspace/text";

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// Fonte única de verdade pra cor por status. Card e badge lêem daqui — sem
// duplicar tons em classes soltas pelo JSX. As classes são literais por causa
// do Tailwind v4 (purge não enxerga string concatenada tipo `border-l-${c}-500`).
//
// Convenção: cada status mapeia pra um tom da paleta padrão do Tailwind
// (border-500 + bg-50/5% com bump em dark). Status "em curso" pegam tons mais
// quentes (amber/yellow/lime), "aguardando" pegam frios (sky/cyan), finais
// pegam verde/vermelho.
const STATUS_STYLES: Record<string, { border: string; bg: string; badge: string }> = {
  novo:                 { border: 'border-l-blue-500',   bg: 'bg-blue-500/5 dark:bg-blue-500/10',     badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  confirmado:           { border: 'border-l-amber-500',  bg: 'bg-amber-500/5 dark:bg-amber-500/10',   badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  enviar_frete:         { border: 'border-l-orange-500', bg: 'bg-orange-500/5 dark:bg-orange-500/10', badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
  em_preparo:           { border: 'border-l-yellow-500', bg: 'bg-yellow-500/5 dark:bg-yellow-500/10', badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  em_separacao:         { border: 'border-l-lime-500',   bg: 'bg-lime-500/5 dark:bg-lime-500/10',     badge: 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300' },
  pronto:               { border: 'border-l-indigo-500', bg: 'bg-indigo-500/5 dark:bg-indigo-500/10', badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300' },
  aguardando_retirada:  { border: 'border-l-sky-500',    bg: 'bg-sky-500/5 dark:bg-sky-500/10',       badge: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300' },
  avisou_motoboy:       { border: 'border-l-cyan-500',   bg: 'bg-cyan-500/5 dark:bg-cyan-500/10',     badge: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300' },
  enviado:              { border: 'border-l-teal-500',   bg: 'bg-teal-500/5 dark:bg-teal-500/10',     badge: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300' },
  entregue:             { border: 'border-l-green-500',  bg: 'bg-green-500/5 dark:bg-green-500/10',   badge: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  cancelado:            { border: 'border-l-red-500',    bg: 'bg-red-500/5 dark:bg-red-500/10',       badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
};

const STATUS_FALLBACK_STYLE = { border: 'border-l-border', bg: '', badge: 'bg-muted text-muted-foreground' };

function statusStyles(status: string | undefined): { border: string; bg: string; badge: string } {
  return (status && STATUS_STYLES[status]) || STATUS_FALLBACK_STYLE;
}

const STATUSES = [
  { id: 'todos', label: 'Todos' },
  { id: 'encomendas', label: 'Encomendas' },
  { id: 'novo', label: 'Novos' },
  { id: 'confirmado', label: 'Confirmados' },
  { id: 'em_preparo', label: 'Em Preparo' },
  { id: 'em_separacao', label: 'Em Separação' },
  { id: 'pronto', label: 'Prontos' },
  { id: 'avisou_motoboy', label: 'Avisou Motoboy' },
  { id: 'enviado', label: 'Enviados/Aguardando' },
  { id: 'entregue', label: 'Entregues' },
  { id: 'cancelado', label: 'Cancelados' },
];

const ALL_ORDER_STATUSES = [
  { id: 'novo', label: 'Pendente' },
  { id: 'confirmado', label: 'Confirmado' },
  { id: 'enviar_frete', label: 'Enviar frete' },
  { id: 'em_preparo', label: 'Em Preparo' },
  { id: 'em_separacao', label: 'Em Separação' },
  { id: 'pronto', label: 'Pronto' },
  { id: 'aguardando_retirada', label: 'Disp. p/ Retirada' },
  { id: 'avisou_motoboy', label: 'Avisou Motoboy' },
  { id: 'enviado', label: 'Saiu p/ Entrega' },
  { id: 'entregue', label: 'Entregue' },
  { id: 'cancelado', label: 'Cancelado' },
];

// Até 27/10/2026 o padrão de «Saiu para entrega» tinha um caminhão (Pedro: «Vamos trocar logo o padrão para um emoji de
// moto»). A tela salva o padrão junto, então o antigo gravado vale como o padrão novo.
const ENVIADO_ANTIGO = "{cliente_nome}, seu pedido #{numero_pedido} saiu para entrega! 🚚";
const STATUS_MESSAGE_DEFAULTS: Record<string, string> = {
  confirmado: "Olá {cliente_nome}! Seu pedido #{numero_pedido} foi confirmado! Iremos preparar tudo para você. 😊",
  em_preparo: "{cliente_nome}, seu pedido #{numero_pedido} está sendo preparado! ⏳",
  em_separacao: "{cliente_nome}, seu pedido #{numero_pedido} está sendo separado! 📦",
  pronto: "{cliente_nome}, seu pedido #{numero_pedido} está pronto! ✅",
  aguardando_retirada: "{cliente_nome}, seu pedido #{numero_pedido} está disponível para retirada! Estamos te esperando. 🏪",
  enviado: "{cliente_nome}, seu pedido #{numero_pedido} saiu para entrega! 🛵",
  entregue: "{cliente_nome}, seu pedido #{numero_pedido} foi entregue! Obrigado pela preferência. ❤️",
  cancelado: "{cliente_nome}, infelizmente seu pedido #{numero_pedido} foi cancelado. Entre em contato conosco se tiver dúvidas.",
  enviar_frete: "Olá {cliente_nome}! Sobre o seu pedido #{numero_pedido}:\n\nProdutos: {total_produtos}\nFrete: {frete}\nTotal atualizado: {total_atualizado}\n{troco}\n\nQualquer dúvida, é só chamar. Obrigado!",
};

function useStoreSettings() {
  return useQuery<any>({
    queryKey: ["settings"],
    queryFn: () => customFetch("/api/settings"),
    staleTime: 60_000,
  });
}

function printOrderFallback(storeName: string, order: any, hideDailyNumber?: boolean, timezone?: string | null, hideGroupTitles?: boolean, orderCodeStyle?: string | null, topMargin?: number | null, showPaidCheckbox?: boolean, footerMessage?: string | null, porCliente?: boolean, receiptFont?: string | null) {
  const receipt = orderToReceipt(order, { hideDailyNumber, timezone, hideGroupTitles, orderCodeStyle, topMargin, showPaidCheckbox, footerMessage, porCliente, receiptFont });
  const html = formatReceiptHTML(storeName, receipt);
  const win = window.open("", "_blank", "width=350,height=600");
  if (!win) return;
  win.document.write(`<!DOCTYPE html><html><head><title>Cupom</title><style>@media print{body{margin:0}}</style></head><body>${html}<script>setTimeout(()=>{window.print();window.close()},300);<\/script></body></html>`);
  win.document.close();
}

function replaceTemplateVars(
  template: string,
  order: any,
  storeName: string,
  overrides?: { freight?: number; productsTotal?: number; updatedTotal?: number; pontos?: string },
  // Toggle "imprimir o número do pedido" da loja. Quando false, as mensagens ao
  // cliente não levam nenhum código (esconde a quantidade de pedidos).
  showOrderNumber: boolean = true,
  orderCodeStyle?: string | null,
): string {
  const clientName = order.customer?.firstName || "Cliente";
  const clientPhone = temWhatsApp(order.customer?.whatsappNumber) ? formatWhatsApp(order.customer?.whatsappNumber) : "";
  const items = (order.items ?? [])
    .map((item: any) => `${item.quantity}x ${item.productName}`)
    .join("\n");
  const orderTotal = Number(order.total) || 0;
  const orderFee = Number(order.deliveryFee) || 0;
  // A taxa de serviço do «Fechar conta» (30/10/2026) também fica fora dos produtos.
  const orderSurcharge = (Number(order.cardSurcharge) || 0) + (Number((order as any).taxaServico) || 0);
  const productsBase = Math.max(0, orderTotal - orderFee - orderSurcharge);

  const freight = overrides?.freight !== undefined ? overrides.freight : orderFee;
  const productsTotal = overrides?.productsTotal !== undefined ? overrides.productsTotal : productsBase;
  const updatedTotal = overrides?.updatedTotal !== undefined ? overrides.updatedTotal : orderTotal;

  const total = formatCurrency(orderTotal);

  const dn = orderDisplayCode(order, orderCodeStyle);
  // {troco}: sobre o total atualizado (o frete que a lojista está digitando entra na conta).
  let result = trocarTroco(template, linhaDoTroco(order, updatedTotal))
    .replace(/\{cliente_nome\}/g, clientName)
    .replace(/\{cliente_numero\}/g, clientPhone)
    .replace(/\{itens\}/g, items)
    .replace(/\{total\}/g, total)
    .replace(/\{loja_nome\}/g, storeName)
    .replace(/\{frete\}/g, formatCurrency(freight))
    .replace(/\{total_produtos\}/g, formatCurrency(productsTotal))
    .replace(/\{total_atualizado\}/g, formatCurrency(updatedTotal))
    // Programa de pontos: frase pronta ("Você ganhou 42 pontos. Saldo: 382 pontos.") ou some.
    .replace(/\s*\{pontos\}/g, overrides?.pontos ? ` ${overrides.pontos}` : "");
  // {numero_pedido}: com toggle ligado → identificador no estilo da loja (o
  // "#" do template fica); desligado → remove "#{numero_pedido}" inteiro.
  result = showOrderNumber
    ? result.replace(/\{numero_pedido\}/g, dn)
    : result.replace(/\s*#?\{numero_pedido\}/g, "");
  return result;
}

export default function Orders() {
  const search = useSearch();
  const [, setLocation] = useLocation();
  const initialStatus = new URLSearchParams(search).get('status') || 'todos';
  const [activeTab, setActiveTab] = useState(initialStatus);
  const { data: orders = [], isLoading, error: listError, refetch: refetchOrders, isFetching: isFetchingOrders } = useListOrders();
  // ⚠️ Lista que FALHOU não é lista vazia. Sem isto o `orders = []` do fallback
  // fazia a tela dizer "Você ainda não recebeu pedidos" (texto de loja nova) e
  // zerava os contadores — numa loja com 68 pedidos e 56 pendentes (relato do
  // Pedro, 19/09/2026: 4 respostas 401 no /api/orders segundos antes do print).
  const falhouRecentes = !!listError;
  const printer = useThermalPrinter();
  const { data: storeSettings } = useStoreSettings();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showFilters, setShowFilters] = useState(false);
  // Filtrou, fecha (Pedro, 10/09): o painel ficava aberto depois da escolha.
  // "Escolher mês…" fecha quando o mês entra. "Escolher período…" só fecha
  // quando o FIM é escolhido: o início já preenche o fim sozinho (PeriodFilter),
  // então "completo" fecharia antes de dar pra ajustar a data final.
  const fechaAoEscolher = (antes: PeriodState, st: PeriodState) => {
    if (st.period === "mes_escolhido") return !!st.pickedMonth;
    if (st.period === "personalizado") return !!st.customFrom && !!st.customTo && st.customFrom === antes.customFrom && st.customTo !== antes.customTo;
    return true;
  };
  const escolherPeriodo = (st: PeriodState) => { const antes = periodState; setPeriodState(st); if (fechaAoEscolher(antes, st)) setShowFilters(false); };
  const escolher = (set: (v: string) => void) => (v: string) => { set(v); setShowFilters(false); };
  const [filterDeliveryMode, setFilterDeliveryMode] = useState("");
  const [filterPaymentMethod, setFilterPaymentMethod] = useState("");
  const [periodState, setPeriodState] = useState<PeriodState>({ period: "hoje", ...EMPTY_PERIOD_STATE });
  const [filterSource, setFilterSource] = useState("");
  // Página abre em "Hoje" (operação do dia); histórico pelo Período dentro dos
  // Filtros ("data" = escolher/digitar um dia específico). A busca (nº do dia
  // ou cliente) ignora o período — procura em tudo que está carregado.
  const [searchTerm, setSearchTerm] = useState("");
  const [exporting, setExporting] = useState(false);

  // "!" no botão Filtros = qualquer desvio do padrão (período default é Hoje).
  const hasFilters = !!filterDeliveryMode || !!filterPaymentMethod || !!filterSource || periodState.period !== "hoje";

  // Filtro de data no SERVIDOR: o GET /api/orders retorna so os 300 mais
  // recentes, entao filtrar por um dia antigo so no client nao achava nada (os
  // pedidos daquele dia ja tinham saido do recorte). Com filtro de data, busca
  // AQUELE dia direto no banco (dateFrom/dateTo), sem o cap de recencia. BRT
  // e sempre -03:00 (Brasil sem horario de verao desde 2019).
  // "Ontem" reusa o caminho do dia específico: um dia cheio pode já ter saído
  // do recorte dos 300 recentes numa loja movimentada.
  const todayStr = brDayKey(new Date());
  const range = resolvePeriod(periodState, todayStr);

  // Filtro de data no SERVIDOR: o GET /api/orders devolve só os 300 mais
  // recentes, então filtrar no client não acha pedido que já saiu do recorte.
  // "Hoje" e "Tudo" seguem no conjunto recente — o primeiro dedupe com o
  // useListOrders() das badges (1 fetch só), o segundo não tem recorte a pedir.
  // BRT é sempre -03:00 (Brasil sem horário de verão desde 2019).
  const buscaNoServidor = periodState.period !== "hoje" && !range.isAll;
  const dateRange = useMemo(
    () =>
      buscaNoServidor
        ? { dateFrom: `${range.dateFrom}T00:00:00.000-03:00`, dateTo: `${range.dateTo}T23:59:59.999-03:00` }
        : undefined,
    [buscaNoServidor, range.dateFrom, range.dateTo],
  );
  const { data: ordersByDate = [], isLoading: isLoadingByDate, error: errorByDate, refetch: refetchByDate, isFetching: isFetchingByDate } = useListOrders(dateRange);
  // Badges do topo (Pedidos Hoje/Pendentes) seguem no conjunto recente.
  const listSource = buscaNoServidor ? ordersByDate : orders;
  const listLoading = buscaNoServidor ? isLoadingByDate : isLoading;
  // O erro que importa pra área da lista é o da consulta que a alimenta.
  const erroDaLista = buscaNoServidor ? errorByDate : listError;
  const falhouLista = !!erroDaLista;
  const recarregarLista = buscaNoServidor ? refetchByDate : refetchOrders;
  const recarregando = buscaNoServidor ? isFetchingByDate : isFetchingOrders;
  // 401 = sessão caiu. Não adianta "tentar de novo": tem que entrar de novo.
  const semSessao = (erroDaLista as { status?: number } | null)?.status === 401;
  // A lista bate no teto de 300 do endpoint; a exportação vai até 5.000.
  const listaTruncada = buscaNoServidor && ordersByDate.length >= 300;

  const paymentMethods = useMemo(() => {
    const methods = new Set<string>();
    orders.forEach(o => { if (o.paymentMethod) methods.add(o.paymentMethod); });
    return Array.from(methods).sort();
  }, [orders]);

  // Tudo MENOS a aba de status — também alimenta as contagens das abas, pra o
  // número de cada aba bater com o que ela mostra sob o período/busca atuais.
  // Fábrica do predicado: os mesmos filtros valem pra lista normal e pra das
  // encomendas — a diferença é só o recorte por data de CRIAÇÃO, que a aba
  // Encomendas ignora (ela recorta pela data da encomenda, no servidor).
  const fazFiltro = (aplicarPeriodo: boolean) => {
    const q = searchTerm.trim();
    const searching = q.length > 0;
    const qNorm = q.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    const qDigits = q.replace(/\D/g, "");
    const today = brDayKey(new Date());
    const sevenDaysAgo = Date.now() - 7 * 86400000;

    return (o: any) => {
      if (filterDeliveryMode && o.deliveryMode !== filterDeliveryMode) return false;
      if (filterPaymentMethod && o.paymentMethod !== filterPaymentMethod) return false;
      // Recorte por INTERVALO (antes era só "aquele dia"). Vale também quando a
      // lista veio do servidor: o endpoint corta por timestamp, aqui a
      // comparação é por dia civil BRT, que é o que o lojista enxerga.
      if (aplicarPeriodo && !range.isAll) {
        const dia = brDayKey(o.createdAt);
        if (dia < range.dateFrom || dia > range.dateTo) return false;
      }
      if (filterSource) {
        const src = (o as any).source || "link";
        if (src !== filterSource) return false;
      }

      if (searching) {
        const byNumber =
          qDigits.length > 0 &&
          (o as any).dailyOrderNumber != null &&
          String((o as any).dailyOrderNumber) === String(parseInt(qDigits, 10));
        // Estilo "codigo" (ou pedido antigo): acha pelo fim do UUID (A1B2).
        const byCode = q.length >= 3 && String(o.id).slice(-4).toUpperCase().includes(q.toUpperCase());
        const name = (o.customer?.firstName ?? "")
          .normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
        const byName = qNorm.length >= 2 && name.includes(qNorm);
        const byWhatsapp = qDigits.length >= 4 && ((o.customer as any)?.whatsappNumber ?? "").includes(qDigits);
        if (!byNumber && !byCode && !byName && !byWhatsapp) return false;
      }
      return true;
    };
  };

  const baseFilteredOrders = useMemo(
    () => listSource.filter(fazFiltro(true)),
    [listSource, filterDeliveryMode, filterPaymentMethod, filterSource, range.dateFrom, range.dateTo, range.isAll, searchTerm],
  );

  // Encomendas: recorte pela DATA DA ENCOMENDA (hoje em diante), no servidor.
  // Filtrar por data de criação deixava a aba vazia — medido 28/08/2026: das
  // 87 encomendas futuras da plataforma, 66 foram feitas em dia anterior.
  const { data: encomendasRaw = [] } = useQuery<any[]>({
    queryKey: ["orders-encomendas", todayStr],
    queryFn: () => customFetch(`/api/orders?scheduledFrom=${todayStr}`),
    staleTime: 30_000,
  });
  const encomendas = useMemo(() => {
    // Mesmo dia: o horário, ou o começo do turno; só a data abre o dia (12/10/2026).
    const horarios = horarioDosTurnos((storeSettings as any)?.orderShiftHours, (storeSettings as any)?.businessHours);
    const proximaData = (o: any) =>
      (o.items ?? [])
        .filter((it: any) => it.scheduledDate)
        .map((it: any) => `${it.scheduledDate}${ordemNoDia(it.scheduledTime, it.scheduledShift, horarios)}`)
        .sort()[0] ?? "";
    return encomendasRaw
      .filter(fazFiltro(false))
      .sort((a, b) => proximaData(a).localeCompare(proximaData(b)));
  }, [encomendasRaw, filterDeliveryMode, filterPaymentMethod, filterSource, searchTerm, storeSettings]);

  const filteredOrders = useMemo(() => {
    if (activeTab === 'encomendas') return encomendas;
    return baseFilteredOrders.filter(o => {
      if (activeTab === 'todos') return true;
      if (activeTab === 'enviado') {
        return o.status === 'enviado' || o.status === 'aguardando_retirada';
      }
      return o.status === activeTab;
    });
  }, [baseFilteredOrders, encomendas, activeTab]);

  const { user } = useAuth();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
  const ordersUrl = user?.store?.slug ? `${origin}${basePath}/loja/${user.store.slug}` : "";

  const todayOrdersCount = orders.filter(o => brDayKey(o.createdAt) === todayStr).length;
  const pendingOrdersCount = orders.filter(o => o.status === 'novo').length;

  // Exporta respeitando os filtros da tela (período/entrega/pagamento/origem).
  // O recorte de datas vai pro servidor — o xlsx não sofre o cap dos 300.
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      // "Tudo" vai sem recorte; qualquer outro período vira o intervalo cheio
      // em BRT. Antes só hoje/ontem/7dias/1 dia tinham tradução — mês e
      // intervalo escolhido nem existiam.
      if (!range.isAll) {
        params.set("dateFrom", `${range.dateFrom}T00:00:00.000-03:00`);
        params.set("dateTo", `${range.dateTo}T23:59:59.999-03:00`);
      }
      if (filterDeliveryMode) params.set("deliveryMode", filterDeliveryMode);
      if (filterPaymentMethod) params.set("paymentMethod", filterPaymentMethod);
      if (filterSource) params.set("source", filterSource);
      const blob = await customFetch<Blob>(`/api/orders/export?${params.toString()}`, { responseType: "blob" } as any);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pedidos_${periodFileTag(range)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: errorTitle("exportar os pedidos"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  // Altura mínima = tela − moldura do AdminLayout (pt-14 + p-4 + pb-20 em
  // <md; p-6/p-8 em md+). Era h-[calc(100svh-2rem)] e o documento ficava
  // 136px mais alto que a tela no celular (vão em branco ao arrastar, medido
  // 10/09). min-h: lista curta fecha em 100svh exato; lista longa cresce e
  // rola a página como sempre. O pb-20 do main já mantém o último pedido
  // acima da nav — o pb-20 daqui dobrava o espaço.
  return (
    <div className="space-y-6 min-h-[calc(100svh-10.5rem)] sm:min-h-[calc(100svh-11.5rem)] md:min-h-[calc(100svh-3rem)] lg:min-h-[calc(100svh-4rem)] flex flex-col">
      <FaixaDoIphone />
      {ordersUrl && (
        <PublicLinkCard
          url={ordersUrl}
          label="Link da Loja:"
          icon={<Package className="w-4 h-4 text-secondary" />}
          shareLabel="nossa loja digital"
        />
      )}

      <div className="grid grid-cols-2 gap-4 shrink-0">
        <Card className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 shrink-0 rounded-full bg-secondary/15 text-secondary flex items-center justify-center">
            <ShoppingBag className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-xl font-bold">{falhouRecentes ? "—" : todayOrdersCount}</p>
            <p className="text-xs text-muted-foreground">Pedidos Hoje</p>
          </div>
        </Card>
        <Card
          className={`p-4 flex items-center gap-3 cursor-pointer transition-all ${activeTab === "novo" ? "ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-950/20" : "hover:bg-muted/50"}`}
          onClick={() => setActiveTab(prev => prev === "novo" ? "todos" : "novo")}
        >
          <div className="w-10 h-10 shrink-0 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center">
            <Clock className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xl font-bold">{falhouRecentes ? "—" : pendingOrdersCount}</p>
            <p className="text-xs text-muted-foreground">Pedidos Pendentes</p>
          </div>
          {activeTab === "novo" && <X className="w-4 h-4 text-blue-500 shrink-0" />}
        </Card>
      </div>

      <div className="flex gap-2 shrink-0">
        <Button variant={hasFilters ? "default" : "outline"} onClick={() => setShowFilters(!showFilters)} className="flex-1">
          <Filter className="w-4 h-4 mr-2" /> Filtros {hasFilters && <span className="ml-1 bg-white/20 text-xs px-1.5 rounded-full">!</span>}
        </Button>
        <Button onClick={() => setLocation("/pdv")} className="flex-1 shadow-md shadow-primary/20">
          <Plus className="w-4 h-4 mr-2" /> Pedido
        </Button>
      </div>

      {/* Busca ativa procura em tudo que está carregado — o período não se aplica. */}
      <div className="relative shrink-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por nº do pedido ou cliente…"
          className="pl-9 pr-9 h-10"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
            aria-label="Limpar busca"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {showFilters && (
        <Card className="p-4 animate-in slide-in-from-top-2 duration-200 shrink-0">
          <div className="flex flex-wrap items-end gap-4">
            <PeriodFilter state={periodState} onChange={escolherPeriodo} testId="orders-filter-period" />
            <div className="space-y-1.5 flex-1 min-w-[140px]">
              <Label className="text-xs">Entrega</Label>
              <select className="w-full border rounded-md p-2 text-sm bg-background" value={filterDeliveryMode} onChange={(e) => escolher(setFilterDeliveryMode)(e.target.value)}>
                <option value="">Todos</option>
                <option value="entrega">Entrega</option>
                <option value="retirada">Retirada</option>
                <option value="combinar">A combinar</option>
              </select>
            </div>
            <div className="space-y-1.5 flex-1 min-w-[140px]">
              <Label className="text-xs">Pagamento</Label>
              <select className="w-full border rounded-md p-2 text-sm bg-background" value={filterPaymentMethod} onChange={(e) => escolher(setFilterPaymentMethod)(e.target.value)}>
                <option value="">Todos</option>
                {paymentMethods.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1.5 flex-1 min-w-[140px]">
              <Label className="text-xs">Origem</Label>
              <select className="w-full border rounded-md p-2 text-sm bg-background" value={filterSource} onChange={(e) => escolher(setFilterSource)(e.target.value)} data-testid="orders-filter-source">
                <option value="">Todos</option>
                <option value="link">Link público</option>
                <option value="pdv">PDV (balcão)</option>
              </select>
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterDeliveryMode(""); setFilterPaymentMethod(""); setFilterSource(""); setPeriodState({ period: "hoje", ...EMPTY_PERIOD_STATE }); setShowFilters(false); }}>
                <X className="w-4 h-4 mr-1" /> Limpar
              </Button>
            )}
            <Button variant="outline" onClick={handleExport} disabled={exporting} className="w-full sm:w-auto" data-testid="orders-export">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Exportar Excel
            </Button>
          </div>
        </Card>
      )}

      {/* Aviso persistente de filtro ativo — sem ele, o default "Hoje" faz
          pedido antigo parecer sumido (confusão real de usuários, jul/2026). */}
      {(() => {
        const otherFilters = [
          filterDeliveryMode && "entrega",
          filterPaymentMethod && "pagamento",
          filterSource && "origem",
        ].filter(Boolean) as string[];
        if (searchTerm) {
          if (otherFilters.length === 0) return null;
          return (
            <div className="flex items-center justify-between gap-2 text-xs bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 text-blue-900 dark:text-blue-200 rounded-lg px-3 py-2 shrink-0">
              <span className="flex items-center gap-1.5 min-w-0">
                <Filter className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">Busca com filtro de {otherFilters.join(", ")} ativo — resultados podem estar incompletos.</span>
              </span>
              <button type="button" onClick={() => { setFilterDeliveryMode(""); setFilterPaymentMethod(""); setFilterSource(""); }} className="underline font-semibold shrink-0">Limpar filtros</button>
            </div>
          );
        }
        // Rótulo do recorte vem do próprio período resolvido — antes só
        // hoje/ontem/7dias/1 dia tinham texto, e mês/intervalo ficavam mudos.
        const periodLabel = range.isAll
          ? ""
          : periodState.period === "hoje"
            ? "só os pedidos de HOJE"
            : range.isRange
              ? `o período ${range.label}`
              : `só o dia ${range.label}`;
        if (!periodLabel && otherFilters.length === 0) return null;
        // No Hoje o aviso fica sempre (Pedro, 28/10/2026): escondido quando havia pedido do dia, ele aparecia com a lista
        // ainda vazia e sumia quando os pedidos chegavam, e a loja achou que os pedidos de ontem tinham sido apagados.
        const filtersSuffix = otherFilters.length > 0 ? ` (+ filtro de ${otherFilters.join(", ")})` : "";
        return (
          <div className="flex flex-col gap-1.5 text-xs bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 text-blue-900 dark:text-blue-200 rounded-lg px-3 py-2 shrink-0" data-testid="orders-filter-banner">
            <span className="flex items-start gap-1.5 min-w-0">
              <Filter className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                {periodLabel ? <>Mostrando {periodLabel}{filtersSuffix} — os demais pedidos estão ocultos.</> : <>Filtro de {otherFilters.join(", ")} ativo — alguns pedidos estão ocultos.</>}
              </span>
            </span>
            <span className="flex items-center gap-4 pl-5">
              {/* "Cadê o pedido de ontem?" é a dúvida nº 1 no default Hoje —
                  atalho pro período Ontem (busca o dia exato no servidor,
                  fora do cap dos 300 recentes). */}
              {periodState.period === "hoje" && (
                <button
                  type="button"
                  onClick={() => setPeriodState({ period: "ontem", ...EMPTY_PERIOD_STATE })}
                  className="underline font-semibold"
                  data-testid="orders-filter-banner-ver-ontem"
                >
                  Ver ontem
                </button>
              )}
              <button
                type="button"
                onClick={() => { setPeriodState({ period: "tudo", ...EMPTY_PERIOD_STATE }); setFilterDeliveryMode(""); setFilterPaymentMethod(""); setFilterSource(""); }}
                className="underline font-semibold"
                data-testid="orders-filter-banner-ver-tudo"
              >
                Ver tudo
              </button>
            </span>
          </div>
        );
      })()}

      {/* O endpoint da lista devolve no máximo 300; a exportação vai até 5.000.
          Num período longo a tela mostra menos pedidos do que o relatório —
          dizer isso é melhor que o lojista achar que sumiu pedido. */}
      {listaTruncada && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 border border-border/60 rounded-lg px-3 py-2 shrink-0">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>Mostrando os 300 pedidos mais recentes do período. O <strong>Exportar Excel</strong> traz todos.</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar shrink-0">
        {STATUSES.map(s => {
          const count = s.id === 'todos'
            ? baseFilteredOrders.length
            : s.id === 'enviado'
              ? baseFilteredOrders.filter(o => o.status === 'enviado' || o.status === 'aguardando_retirada').length
              : s.id === 'encomendas'
                ? encomendas.length
                : baseFilteredOrders.filter(o => o.status === s.id).length;
          return (
            <button
              key={s.id}
              onClick={() => setActiveTab(s.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                activeTab === s.id
                  ? 'bg-secondary text-secondary-foreground shadow-md'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted'
              }`}
            >
              {s.label}
              {count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${activeTab === s.id ? 'bg-background/20' : 'bg-background shadow-sm'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Order List */}
      <SectionErrorBoundary label="Lista de pedidos">
        <div className="flex-1 overflow-visible">
          {listLoading ? (
            <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : falhouLista ? (
            // Erro de carga tem tela PRÓPRIA: dizer "você ainda não recebeu
            // pedidos" pra quem só perdeu a conexão (ou a sessão) parece perda
            // de dados. Os pedidos continuam no servidor.
            <div className="bg-card rounded-xl border border-border/50 border-dashed">
              <RegistroDoAviso tela="Pedidos" erro={erroDaLista} />
              <EmptyState
                icon={semSessao ? LogIn : WifiOff}
                title={semSessao ? "Sua sessão expirou" : "Não consegui carregar seus pedidos"}
                description={
                  semSessao
                    ? "Entre de novo pra ver seus pedidos."
                    : "Parece que a conexão falhou. Tente de novo."
                }
                action={
                  semSessao ? (
                    <Button onClick={() => { registrarAviso("entrar tela=Pedidos", true); window.location.href = `${import.meta.env.BASE_URL.replace(/\/$/, "")}/login`; }}>
                      <LogIn className="w-4 h-4 mr-1" /> Entrar de novo
                    </Button>
                  ) : (
                    <Button onClick={() => void tentarERegistrar("Pedidos", erroDaLista, () => recarregarLista())} disabled={recarregando}>
                      {recarregando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                      Tentar de novo
                    </Button>
                  )
                }
              />
            </div>
          ) : filteredOrders.length === 0 ? (
            // "Sem pedidos ainda" no admin é o pior momento pro lojista novo —
            // tela em branco mata ativação. Vira CTA "Compartilhe o link da sua
            // loja". Demais abas (em rota/entregue/etc) ficam explicativas — não
            // são onboarding, são fluxos vazios momentâneos.
            activeTab === 'todos' && orders.length === 0 ? (
              <div className="bg-card rounded-xl border border-border/50 border-dashed">
                <EmptyState
                  icon={ShoppingBag}
                  title="Você ainda não recebeu pedidos"
                  description="Compartilhe o link da sua loja no WhatsApp pra começar a vender. Quando entrar pedido, aparece aqui em tempo real."
                  action={ordersUrl && (
                    <>
                      <Button
                        variant="default"
                        onClick={() => {
                          const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(`Confira nosso cardápio: ${ordersUrl}`)}`;
                          abreWhatsApp(url);
                        }}
                      >
                        <Share2 className="w-4 h-4 mr-1" /> Compartilhar no WhatsApp
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(ordersUrl);
                          toast({ title: "Link copiado", description: ordersUrl });
                        }}
                      >
                        <Copy className="w-4 h-4 mr-1" /> Copiar link
                      </Button>
                    </>
                  )}
                />
              </div>
            ) : (
              <div className="bg-card rounded-xl border border-border/50 border-dashed">
                <EmptyState
                  icon={Search}
                  title={searchTerm ? "Nenhum pedido encontrado" : periodState.period === "hoje" && activeTab === 'todos' ? "Nenhum pedido hoje (ainda!)" : "Nenhum pedido nesta etapa"}
                  description={
                    searchTerm
                      ? 'Confira o número ou o nome. Pedidos bem antigos só aparecem pelo filtro de data.'
                      : periodState.period === "hoje" && activeTab === 'todos'
                        ? 'Abra os Filtros e mude o Período pra ver pedidos anteriores.'
                        : activeTab === 'todos'
                          ? 'Ajuste os filtros pra ver pedidos de outros períodos ou status.'
                          : `Pedidos que mudarem para o status "${STATUSES.find(s => s.id === activeTab)?.label}" aparecerão aqui.`
                  }
                />
              </div>
            )
          ) : (
            /* Colunas pela LARGURA ÚTIL, não pela da janela: com a barra lateral (256px)
               as colunas fixas por breakpoint davam card de 224-228px em 1024/1280 e o
               preço invadia o card vizinho (Cazzela, notebook, 02/10/2026). */
            <div className="grid grid-cols-[repeat(auto-fill,minmax(min(280px,100%),1fr))] gap-4 pb-4">
              {filteredOrders.map(order => (
                <SectionErrorBoundary key={order.id} label={`Pedido #${(order.id || '').slice(-4).toUpperCase()}`} resetKeys={[order.id, order.status]} compact>
              <OrderCard order={order} printer={printer} storeName={storeSettings?.name || "Minha Loja"} />
            </SectionErrorBoundary>
              ))}
            </div>
          )}
        </div>
      </SectionErrorBoundary>

    </div>
  );
}

function StatusDropdown({ currentStatus, onSelect, isPending, hideStatuses }: {
  currentStatus: string;
  onSelect: (status: string) => void;
  isPending: boolean;
  hideStatuses?: string[];
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateRect = useCallback(() => {
    if (buttonRef.current) setRect(buttonRef.current.getBoundingClientRect());
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
        menuRef.current && !menuRef.current.contains(e.target as Node)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open, updateRect]);

  const handleOpen = () => {
    updateRect();
    setOpen(v => !v);
  };

  const visibleStatuses = ALL_ORDER_STATUSES.filter(s => !hideStatuses?.includes(s.id) || s.id === currentStatus);

  const current = visibleStatuses.find(s => s.id === currentStatus) || ALL_ORDER_STATUSES.find(s => s.id === currentStatus);

  const menuHeight = visibleStatuses.length * 42;
  const spaceAbove = rect ? rect.top : 0;
  const spaceBelow = rect ? window.innerHeight - rect.bottom : 0;
  const openAbove = spaceAbove > spaceBelow && spaceAbove >= menuHeight;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        disabled={isPending}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border bg-background text-sm font-medium hover:bg-muted transition-colors disabled:opacity-60"
      >
        <span className="truncate">{current?.label ?? currentStatus}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && rect && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
            ...(openAbove
              ? { bottom: window.innerHeight - rect.top + 4 }
              : { top: rect.bottom + 4 }),
          }}
          className="bg-card border border-border rounded-xl shadow-xl overflow-hidden"
        >
          {visibleStatuses.map(s => {
            const isActive = currentStatus === s.id;
            const isCancelado = s.id === 'cancelado';
            return (
              <button
                key={s.id}
                onClick={() => {
                  if (!isActive) { onSelect(s.id); setOpen(false); }
                }}
                disabled={isActive}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm text-left transition-colors ${
                  isActive
                    ? 'bg-secondary/15 text-secondary font-semibold cursor-default'
                    : isCancelado
                      ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30'
                      : 'text-foreground hover:bg-muted'
                }`}
              >
                {s.label}
                {isActive && <Check className="w-3.5 h-3.5 shrink-0" />}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

function WhatsAppMessageModal({
  order,
  newStatus,
  message,
  onMessageChange,
  onSendAndChange,
  onChangeOnly,
  onClose,
  isPending,
}: {
  order: any;
  newStatus: string;
  message: string;
  onMessageChange: (msg: string) => void;
  onSendAndChange: () => void;
  onChangeOnly: () => void;
  onClose: () => void;
  isPending: boolean;
}) {
  const clientName = order.customer?.firstName || "Cliente";
  const statusLabel = ALL_ORDER_STATUSES.find(s => s.id === newStatus)?.label || newStatus;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-card rounded-2xl shadow-2xl w-full max-w-lg border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="font-bold text-lg">Enviar mensagem para {clientName}?</h3>
            <p className="text-sm text-muted-foreground mt-1">{temWhatsApp(order.customer?.whatsappNumber) ? formatWhatsApp(order.customer?.whatsappNumber) : "Sem WhatsApp"}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Novo status:</span>
            <span className="font-semibold text-foreground">{statusLabel}</span>
          </div>

          <textarea
            value={message}
            onChange={(e) => onMessageChange(e.target.value)}
            rows={5}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none [field-sizing:content]"
          />
        </div>

        <div className="p-4 border-t flex flex-col sm:flex-row gap-2">
          <Button
            onClick={onSendAndChange}
            disabled={isPending}
            className="flex-1 cursor-pointer"
            style={{ backgroundColor: "#25D366" }}
          >
            {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />}
            Enviar e Alterar Status
          </Button>
          <Button
            variant="outline"
            onClick={onChangeOnly}
            disabled={isPending}
            className="flex-1 cursor-pointer"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Alterar sem Enviar
          </Button>
        </div>
        <OpcaoAppWhatsApp />
      </div>
    </div>,
    document.body
  );
}

interface MotoboyData {
  id: string;
  storeId: string;
  name: string;
  whatsappNumber: string;
  createdAt: string;
}

function useMotoboys() {
  return useQuery<MotoboyData[]>({
    queryKey: ["motoboys"],
    queryFn: () => customFetch("/api/motoboys"),
  });
}

// Ponto que o cliente marcou no checkout (GPS, pino ou busca). O texto pode
// ser ambíguo — CEP de conjunto cobre várias quadras — e o ponto não é.
function mapsLinkFor(addr: unknown): string | null {
  const a = addr as { lat?: number | string | null; lng?: number | string | null } | null | undefined;
  if (a?.lat == null || a?.lng == null) return null;
  const lat = Number(a.lat);
  const lng = Number(a.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return `https://www.google.com/maps/search/?api=1&query=${lat.toFixed(6)},${lng.toFixed(6)}`;
}

const MOTOBOY_MSG_DEFAULT_TEMPLATE = `Olá {motoboy_nome}! Pedido *#{numero_pedido}* pronto para entrega! 🏍️

*Cliente:* {cliente_nome}
*Tel:* {cliente_whatsapp}

*Endereço:*
{endereco}

*Itens:*
{itens}

*Forma de pagamento:* {pagamento}
*Valor a cobrar:* {valor_cobrar}
{troco}
{obs}`;

function SendFreightModal({
  order,
  storeSettings,
  onClose,
  onSubmit,
  isPending,
}: {
  order: any;
  storeSettings: any;
  onClose: () => void;
  onSubmit: (args: { freight: number; updatedTotal: number; productsTotal: number; message: string; sendWhatsApp: boolean }) => void;
  isPending: boolean;
}) {
  const orderTotal = Number(order.total) || 0;
  const orderFee = Number(order.deliveryFee) || 0;
  // Acréscimo do cartão + taxa de serviço do PDV: ficam fora dos produtos e voltam no total atualizado.
  const orderSurcharge = (Number(order.cardSurcharge) || 0) + (Number((order as any).taxaServico) || 0);
  const productsTotal = Math.max(0, orderTotal - orderFee - orderSurcharge);

  const initialFee = orderFee > 0 ? orderFee : 0;
  const [freightStr, setFreightStr] = useState<string>(initialFee > 0 ? initialFee.toFixed(2).replace(".", ",") : "");
  const [messageDirty, setMessageDirty] = useState(false);
  const [message, setMessage] = useState<string>("");

  const parseFreight = (v: string): number => {
    const digits = v.replace(/\D/g, "");
    if (!digits) return 0;
    return parseInt(digits, 10) / 100;
  };
  const formatFreightInput = (v: string): string => {
    const digits = v.replace(/\D/g, "");
    if (!digits) return "";
    const n = parseInt(digits, 10) / 100;
    return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const freight = parseFreight(freightStr);
  const updatedTotal = productsTotal + orderSurcharge + freight;

  const templates = storeSettings?.statusMessageTemplates;
  const entry = templates?.["enviar_frete"];
  const rawTemplate = comLinhaDoTroco(entry?.template || STATUS_MESSAGE_DEFAULTS["enviar_frete"], "{total_atualizado}");
  const computedMessage = replaceTemplateVars(rawTemplate, order, storeSettings?.name || "", { freight, productsTotal, updatedTotal }, storeSettings?.dailyOrderNumbering !== false, (storeSettings as any)?.orderCodeStyle);

  useEffect(() => {
    if (!messageDirty) setMessage(computedMessage);
  }, [computedMessage, messageDirty]);

  const clientName = order.customer?.firstName || "Cliente";
  const valid = freight > 0;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-card rounded-2xl shadow-2xl w-full max-w-lg border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="font-bold text-lg">Enviar frete para {clientName}</h3>
            <p className="text-sm text-muted-foreground mt-1">{temWhatsApp(order.customer?.whatsappNumber) ? formatWhatsApp(order.customer?.whatsappNumber) : "Sem WhatsApp"}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="space-y-1.5">
            <Label className="text-sm">Valor do frete (R$)</Label>
            <Input
              inputMode="numeric"
              placeholder="Ex: 12,00"
              value={formatFreightInput(freightStr)}
              onChange={(e) => setFreightStr(e.target.value)}
              className="h-11"
            />
            {!valid && (
              <p className="text-xs text-muted-foreground">Informe o valor do frete para continuar.</p>
            )}
          </div>

          <div className="rounded-lg bg-muted/40 border border-border/40 p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Produtos</span><span>{formatCurrency(productsTotal)}</span></div>
            {orderSurcharge > 0 && (
              <div className="flex justify-between"><span className="text-muted-foreground">Acréscimo</span><span>{formatCurrency(orderSurcharge)}</span></div>
            )}
            <div className="flex justify-between"><span className="text-muted-foreground">Frete</span><span>{formatCurrency(freight)}</span></div>
            <div className="flex justify-between font-semibold border-t pt-1 mt-1"><span>Total atualizado</span><span className="text-primary">{formatCurrency(updatedTotal)}</span></div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Mensagem para o cliente</Label>
              {messageDirty && (
                <button
                  type="button"
                  onClick={() => { setMessageDirty(false); setMessage(computedMessage); }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >Restaurar</button>
              )}
            </div>
            <textarea
              value={message}
              onChange={(e) => { setMessageDirty(true); setMessage(e.target.value); }}
              rows={7}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none [field-sizing:content]"
            />
          </div>
        </div>

        <div className="p-4 border-t flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => onSubmit({ freight, updatedTotal, productsTotal, message, sendWhatsApp: true })}
            disabled={!valid || isPending}
            className="flex-1 cursor-pointer"
            style={{ backgroundColor: "#25D366" }}
          >
            {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />}
            Enviar e Atualizar
          </Button>
          <Button
            variant="outline"
            onClick={() => onSubmit({ freight, updatedTotal, productsTotal, message, sendWhatsApp: false })}
            disabled={!valid || isPending}
            className="flex-1 cursor-pointer"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Atualizar sem Enviar
          </Button>
        </div>
        <OpcaoAppWhatsApp />
      </div>
    </div>,
    document.body
  );
}

function MotoboyMessageModal({
  order,
  onStatusChanged,
  onClose,
  isPending,
  updateMutation,
}: {
  order: any;
  onStatusChanged: () => void;
  onClose: () => void;
  isPending: boolean;
  updateMutation: any;
}) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { data: storeSettings } = useStoreSettings();
  const { data: motoboys = [], isLoading } = useMotoboys();
  const [selectedMotoboyId, setSelectedMotoboyId] = useState("");
  const [message, setMessage] = useState("");
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [showNewMotoboy, setShowNewMotoboy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newWhatsapp, setNewWhatsapp] = useState("");
  const [newDdi, setNewDdi] = useState("55");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editWhatsapp, setEditWhatsapp] = useState("");
  const [editDdi, setEditDdi] = useState("55");

  const [whatsappError, setWhatsappError] = useState("");

  const parseDdiFromNumber = (whatsappNumber: string) => {
    const digits = whatsappNumber.replace(/\D/g, "");
    if (digits.startsWith("55") && digits.length >= 12) return { ddi: "55", local: formatWhatsApp(digits.slice(2)) };
    return { ddi: "55", local: formatWhatsApp(digits) };
  };

  const buildMessage = (motoboy: MotoboyData | undefined) => {
    const templateEntry = storeSettings?.statusMessageTemplates?.["avisou_motoboy"];
    const template = comLinhaDoTroco(templateEntry?.template || MOTOBOY_MSG_DEFAULT_TEMPLATE, "{valor_cobrar}");

    // Motoboy é interno (não é o cliente) — sempre leva o identificador.
    const orderNum = orderDisplayCode(order, (storeSettings as any)?.orderCodeStyle);
    const clientName = order.customer?.firstName || "Cliente";
    const clientWhatsapp = temWhatsApp(order.customer?.whatsappNumber) ? formatWhatsApp(order.customer?.whatsappNumber) : "";
    const addr = order.address;
    const neighborhoodOnly = addr && !addr.zipCode && !addr.city;
    const enderecoStr = addr
      ? neighborhoodOnly
        ? `${addr.street}, ${addr.number}${addr.complement ? ` - ${addr.complement}` : ""}${addr.neighborhood ? `\nBairro: ${addr.neighborhood}` : ""}`
        : `${addr.street}, ${addr.number}${addr.complement ? ` - ${addr.complement}` : ""} - ${addr.neighborhood}${addr.city ? `, ${addr.city}` : ""}${addr.zipCode ? ` - CEP: ${addr.zipCode}` : ""}`
      : order.deliveryMode === "combinar" ? "A combinar" : "Retirada na loja";
    // Ponto de referência que o cliente escreveu no checkout (Pedro, 23/10/2026, consulta #640: a lojista mandava à
    // parte): mesmo rótulo da mensagem do cliente, depois do endereço e antes da localização. Vazio não gera linha.
    const referencia = addr ? String((addr as { reference?: string | null }).reference ?? "").trim() : "";
    const enderecoComReferencia = referencia ? `${enderecoStr}\n*Referência:* ${referencia}` : enderecoStr;
    const motoboyMapsLink = addr ? mapsLinkFor(addr) : null;
    const enderecoComMapa = motoboyMapsLink ? `${enderecoComReferencia}\n*Localização:* ${motoboyMapsLink}` : enderecoComReferencia;
    // Pedido para presente: quem recebe vem antes do endereço, em qualquer modelo (a loja não precisa de variável nova).
    const presentePara = (order as { giftRecipient?: string | null }).giftRecipient;
    const enderecoDoMotoboy = presentePara ? `*Presente para:* ${presentePara}\n${enderecoComMapa}` : enderecoComMapa;

    const items = (order.items ?? []).map((item: any) => {
      const optionsTotal = Number(item.optionsTotal) || 0;
      const unitPrice = (Number(item.price) || 0) + optionsTotal;
      const subtotal = unitPrice * (item.quantity || 1);
      let line = `  ${item.quantity}x ${item.productName} - ${formatCurrency(subtotal)}`;
      return line;
    }).join("\n");

    const total = Number(order.total) || 0;
    // Pedido já pago (PIX/cartão adiantado): o motoboy não cobra — a loja
    // ligava isso escrevendo "PAGO" na comanda à mão.
    const valorCobrar = alreadyPaid ? "JÁ PAGO — não cobrar" : formatCurrency(total);
    // Quanto o motoboy leva de troco, sobre o total salvo (já com o frete).
    const troco = alreadyPaid ? "" : linhaDoTroco(order, total, { negrito: true });
    const pagamento = alreadyPaid
      ? `${order.paymentMethod || "Não informado"} (já pago)`
      : order.paymentMethod || "Não informado";
    const obs = order.notes ? `*Obs:* ${order.notes}` : "";
    const taxaEntregaNum = Number(order.deliveryFee) || 0;
    // Frete ainda não fechado não é "Grátis" — quem recebe esta mensagem é o
    // motoboy, que é justamente quem cobra.
    const taxaEntrega = (order as any).deliveryFeePending
      ? "a calcular"
      : taxaEntregaNum > 0 ? formatCurrency(taxaEntregaNum) : "Grátis";

    return trocarTroco(template, troco)
      .replace(/\{motoboy_nome\}/g, motoboy?.name || "")
      .replace(/\{numero_pedido\}/g, orderNum)
      .replace(/\{cliente_nome\}/g, clientName)
      .replace(/\{cliente_whatsapp\}/g, clientWhatsapp)
      .replace(/\{endereco\}/g, enderecoDoMotoboy)
      .replace(/\{itens\}/g, items)
      .replace(/\{pagamento\}/g, pagamento)
      .replace(/\{valor_cobrar\}/g, valorCobrar)
      .replace(/\{taxa_entrega\}/g, taxaEntrega)
      .replace(/\{obs\}/g, obs);
  };

  useEffect(() => {
    if (selectedMotoboyId) {
      const m = motoboys.find(mb => mb.id === selectedMotoboyId);
      setMessage(buildMessage(m));
    }
  }, [selectedMotoboyId, motoboys, alreadyPaid]);

  const createMutation = useMutation({
    mutationFn: (data: { name: string; whatsappNumber: string; ddi: string }) =>
      customFetch("/api/motoboys", { method: "POST", body: JSON.stringify(data), headers: { "Content-Type": "application/json" } }),
    onSuccess: (newMotoboy: MotoboyData) => {
      queryClient.invalidateQueries({ queryKey: ["motoboys"] });
      setShowNewMotoboy(false);
      setNewName("");
      setNewWhatsapp("");
      setNewDdi("55");
      setWhatsappError("");
      setSelectedMotoboyId(newMotoboy.id);
    },
    onError: (err: any) => {
      setWhatsappError(extractApiErrorMessage(err, "Não foi possível verificar o WhatsApp — tente de novo em instantes."));
    },
  });

  const updateMotoboyMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; whatsappNumber: string; ddi: string } }) =>
      customFetch(`/api/motoboys/${id}`, { method: "PUT", body: JSON.stringify(data), headers: { "Content-Type": "application/json" } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["motoboys"] });
      setEditingId(null);
      setEditName("");
      setEditWhatsapp("");
      setEditDdi("55");
      setWhatsappError("");
    },
    onError: (err: any) => {
      setWhatsappError(extractApiErrorMessage(err, "Não foi possível verificar o WhatsApp — tente de novo em instantes."));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      customFetch(`/api/motoboys/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["motoboys"] });
      if (selectedMotoboyId) setSelectedMotoboyId("");
    },
  });

  const handleSendAndChange = () => {
    const motoboy = motoboys.find(m => m.id === selectedMotoboyId);
    if (!motoboy) return;
    const phone = motoboy.whatsappNumber.replace(/\D/g, "");
    const text = encodeURIComponent(message);
    abreWhatsApp(`https://api.whatsapp.com/send?phone=${phone}&text=${text}`);
    updateMutation.mutate({ id: order.id, data: { status: "avisou_motoboy" } });
  };

  const handleChangeOnly = () => {
    updateMutation.mutate({ id: order.id, data: { status: "avisou_motoboy" } });
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-card rounded-2xl shadow-2xl w-full max-w-lg border overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2">
              <Bike className="w-5 h-5 text-sky-600" />
              Avisar Motoboy
            </h3>
            <p className="text-sm text-muted-foreground mt-1">Pedido #{orderDisplayCode(order, (storeSettings as any)?.orderCodeStyle)}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2 font-semibold">
              <Bike className="w-4 h-4 text-muted-foreground" />
              Motoboy
            </Label>

            {showNewMotoboy ? (
              <div className="space-y-2 p-3 bg-muted/30 rounded-lg border">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Nome do motoboy"
                  className="bg-card"
                  autoFocus
                />
                <WhatsAppInput
                  value={newWhatsapp}
                  onChange={(v) => { setNewWhatsapp(v); setWhatsappError(""); }}
                  ddi={newDdi}
                  onDdiChange={setNewDdi}
                />
                {whatsappError && (
                  <p className="text-xs text-destructive">{whatsappError}</p>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="bg-primary hover:bg-primary/90 text-white"
                    onClick={() => {
                      if (newName.trim() && newWhatsapp.trim()) {
                        setWhatsappError("");
                        createMutation.mutate({ name: newName.trim(), whatsappNumber: newWhatsapp.replace(/\D/g, ""), ddi: newDdi });
                      }
                    }}
                    disabled={!newName.trim() || !newWhatsapp.trim() || createMutation.isPending}
                  >
                    {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    {createMutation.isPending ? "Verificando..." : "Criar"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => { setShowNewMotoboy(false); setNewName(""); setNewWhatsapp(""); setNewDdi("55"); setWhatsappError(""); }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : editingId ? (
              <div className="space-y-2 p-3 bg-muted/30 rounded-lg border">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Nome do motoboy"
                  className="bg-card"
                  autoFocus
                />
                <WhatsAppInput
                  value={editWhatsapp}
                  onChange={(v) => { setEditWhatsapp(v); setWhatsappError(""); }}
                  ddi={editDdi}
                  onDdiChange={setEditDdi}
                />
                {whatsappError && (
                  <p className="text-xs text-destructive">{whatsappError}</p>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="bg-primary hover:bg-primary/90 text-white"
                    onClick={() => {
                      if (editName.trim() && editWhatsapp.trim()) {
                        setWhatsappError("");
                        updateMotoboyMutation.mutate({ id: editingId, data: { name: editName.trim(), whatsappNumber: editWhatsapp.replace(/\D/g, ""), ddi: editDdi } });
                      }
                    }}
                    disabled={!editName.trim() || !editWhatsapp.trim() || updateMotoboyMutation.isPending}
                  >
                    {updateMotoboyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    {updateMotoboyMutation.isPending ? "Verificando..." : "Salvar"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => { setEditingId(null); setEditName(""); setEditWhatsapp(""); setEditDdi("55"); setWhatsappError(""); }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <select
                  className="flex-1 min-w-0 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={selectedMotoboyId}
                  onChange={(e) => setSelectedMotoboyId(e.target.value)}
                >
                  <option value="">Selecionar motoboy...</option>
                  {motoboys.map(m => (
                    <option key={m.id} value={m.id}>{m.name} - {formatWhatsApp(m.whatsappNumber)}</option>
                  ))}
                </select>
                {selectedMotoboyId ? (
                  <>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="shrink-0 h-9 w-9 text-primary hover:text-primary hover:bg-primary/10"
                      onClick={() => {
                        const m = motoboys.find(mb => mb.id === selectedMotoboyId);
                        if (m) {
                          const parsed = parseDdiFromNumber(m.whatsappNumber);
                          setEditingId(m.id);
                          setEditName(m.name);
                          setEditWhatsapp(parsed.local);
                          setEditDdi(parsed.ddi);
                        }
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="shrink-0 h-9 w-9 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={async () => {
                        if (await confirm({ title: "Excluir este motoboy?", confirmLabel: "Excluir" })) {
                          deleteMutation.mutate(selectedMotoboyId);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="shrink-0 h-9 w-9"
                    onClick={() => setShowNewMotoboy(true)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
          </div>

          {selectedMotoboyId && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-sm font-medium">Pedido já pago</Label>
                <Switch
                  className="shrink-0"
                  checked={alreadyPaid}
                  onCheckedChange={setAlreadyPaid}
                  data-testid="motoboy-already-paid"
                />
              </div>
              <Label className="text-sm font-medium">Mensagem para o motoboy</Label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={12}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none [field-sizing:content]"
              />
            </div>
          )}
        </div>

        <div className="p-4 border-t flex flex-col sm:flex-row gap-2">
          <Button
            onClick={handleSendAndChange}
            disabled={isPending || !selectedMotoboyId}
            className="flex-1 cursor-pointer"
            style={{ backgroundColor: "#25D366" }}
          >
            {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />}
            Enviar e Alterar Status
          </Button>
          <Button
            variant="outline"
            onClick={handleChangeOnly}
            disabled={isPending}
            className="flex-1 cursor-pointer"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Alterar sem Enviar
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function OrderCard({ order, printer, storeName }: { order: any; printer: ReturnType<typeof useThermalPrinter>; storeName: string }) {
  const { toast } = useToast();
  // Pedido trazido de outro cardápio pela Configuradora (T-0033, 01/11/2026): só histórico. A API recusa status, forma de
  // pagamento e edição (409) e a fila de impressão (404); excluir continua.
  const importado = order.source === "importado";
  const { data: whatsappBot } = useWhatsappBot();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { data: storeSettings } = useStoreSettings();
  // Loja desligou "imprimir o número do pedido" → não sai no cupom.
  const hideDailyNumber = (storeSettings as any)?.dailyOrderNumbering === false;
  const hideGroupTitles = (storeSettings as any)?.showGroupTitles === false;
  const storeTimezone = ((storeSettings as any)?.timezone as string | undefined) ?? null;
  const { user: authUser } = useAuth();
  const isDev = (authUser as any)?.user?.role === "dev";
  const [modalState, setModalState] = useState<{ status: string; message: string } | null>(null);
  const [showMotoboyModal, setShowMotoboyModal] = useState(false);
  const [showFreightModal, setShowFreightModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState<any | null>(null);
  const [printing, setPrinting] = useState(false);

  const storeFeeType: string = (storeSettings as any)?.deliveryFeeType || "fixed";
  // O frete pendente é do PEDIDO, não do tipo da loja: uma loja por bairro
  // pode ter escolhido "calcular depois" só pro bairro fora da lista. Sem
  // olhar a flag do pedido, esse pedido chegaria sem taxa e sem onde preencher.
  const supportsFreightStep =
    order.deliveryMode === "entrega" &&
    (storeFeeType === "on_request" || (order as any).deliveryFeePending === true);

  const isPrinted = printer.printedOrderIds.has(order.id);
  const isInQueue = printer.queue.some(q => q.orderId === order.id);
  // O ícone vai pela térmica (USB conectada) ou pela fila (app ou térmica que a loja já ligou). Loja que nunca ligou
  // impressora não tem quem imprima a fila: abre a janela de impressão do aparelho, como no Firefox. Antes ia para a
  // fila mesmo assim e dizia «Adicionado à fila de impressão» sem nada entrar (#366). Pedido que não entra na fila (o
  // POST falha ou volta `skipped`) também vai pela janela: o printData devolve "failed".
  const vaiPelaJanela = !(printer.connected || (printer.supported && printer.printerEverConnected));

  const handlePrint = async () => {
    if (isPrinted) {
      if (!(await confirm({
        title: "Imprimir de novo?",
        description: "Este pedido já foi impresso.",
        confirmLabel: "Imprimir",
        destructive: false,
      }))) return;
    }
    setPrinting(true);
    try {
      if (!vaiPelaJanela) {
        const receipt = orderToReceipt(order, { hideDailyNumber, timezone: storeTimezone, hideGroupTitles, orderCodeStyle: (storeSettings as any)?.orderCodeStyle, topMargin: (storeSettings as any)?.receiptTopMargin, showPaidCheckbox: (storeSettings as any)?.receiptPaidCheckbox === true, receiptFont: (storeSettings as any)?.pdvSettings?.receiptFont ?? null, footerMessage: (storeSettings as any)?.receiptFooterPrinted ?? null, porCliente: (storeSettings as any)?.tableQrPerClient === true });
        const data = formatReceiptBytes(storeName, receipt, printer.prefs.paperWidth, printer.isAsciiOnlyPrinter);
        const result = await printer.printData(data, order.id);
        if (result === "printed") toast({ title: "Cupom impresso!" });
        // A conta dev não põe na fila das lojas que abre (o enqueueOrder nem manda o pedido): diz isso, sem janela.
        else if (isDev) toast({ title: "Sua conta de desenvolvedor não imprime nas lojas" });
        else if (result === "queued") toast({ title: "Adicionado à fila de impressão" });
        else {
          printOrderFallback(storeName, order, hideDailyNumber, storeTimezone, hideGroupTitles, (storeSettings as any)?.orderCodeStyle, (storeSettings as any)?.receiptTopMargin, (storeSettings as any)?.receiptPaidCheckbox === true, (storeSettings as any)?.receiptFooterPrinted ?? null, (storeSettings as any)?.tableQrPerClient === true, (storeSettings as any)?.pdvSettings?.receiptFont ?? null);
        }
      } else {
        printOrderFallback(storeName, order, hideDailyNumber, storeTimezone, hideGroupTitles, (storeSettings as any)?.orderCodeStyle, (storeSettings as any)?.receiptTopMargin, (storeSettings as any)?.receiptPaidCheckbox === true, (storeSettings as any)?.receiptFooterPrinted ?? null, (storeSettings as any)?.tableQrPerClient === true, (storeSettings as any)?.pdvSettings?.receiptFont ?? null);
      }
    } catch {
      toast({ title: errorTitle("imprimir"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
    }
    setPrinting(false);
  };

  const updateMutation = useUpdateOrderStatus({
    mutation: {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: getListOrdersQueryKey() });
        toast({ title: "Status atualizado!" });
        setModalState(null);
        setShowMotoboyModal(false);
        setShowFreightModal(false);
        // Caminho LEGADO, só enquanto a loja não tiver a preferência no servidor.
        // Quando ela tem, quem enfileira o `status:<x>` é o PUT /:id/status —
        // e aí imprimir daqui TAMBÉM duplicaria o cupom: o agente imprime e dá
        // ack em <1s, o ack DELETA a linha e o unique (store, order, device)
        // para de segurar o segundo insert (incidente 10-11/06/2026).
        // Não dá pra simplesmente apagar este bloco: loja que imprime só pela
        // USB do navegador nunca gravou nada no servidor e não deixa rastro no
        // banco — some da telemetria inteira. Ela segue por aqui até o lojista
        // encostar no toggle (o que grava o espelho e passa a bola pro servidor).
        const serverOwnsStatusPrint =
          typeof (storeSettings as any)?.pdvSettings?.autoPrintStatusChange === "boolean";
        const newStatus = (variables as any)?.data?.status as string | undefined;
        const list = printer.prefs.autoPrintStatusList || [];
        if (!serverOwnsStatusPrint && !isDev && printer.prefs.autoPrintStatusChange && newStatus && list.includes(newStatus)) {
          const receipt = orderToReceipt({ ...order, status: newStatus }, { hideDailyNumber, timezone: storeTimezone, hideGroupTitles, orderCodeStyle: (storeSettings as any)?.orderCodeStyle, topMargin: (storeSettings as any)?.receiptTopMargin, showPaidCheckbox: (storeSettings as any)?.receiptPaidCheckbox === true, receiptFont: (storeSettings as any)?.pdvSettings?.receiptFont ?? null, footerMessage: (storeSettings as any)?.receiptFooterPrinted ?? null, porCliente: (storeSettings as any)?.tableQrPerClient === true });
          const data = formatReceiptBytes(storeName, receipt, printer.prefs.paperWidth, printer.isAsciiOnlyPrinter);
          printer.printData(data, order.id, `status:${newStatus}`);
        }
      }
    }
  });

  const deleteOrderMutation = useMutation({
    mutationFn: () => customFetch(`/api/orders/${order.id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListOrdersQueryKey() });
      toast({ title: "Pedido excluído" });
    },
    onError: (err: any) => {
      toast({ title: errorTitle("excluir o pedido"), description: extractApiErrorMessage(err), variant: "destructive" });
    },
  });

  const handleDeleteOrder = async () => {
    if (deleteOrderMutation.isPending) return;
    if (await confirm({
      title: `Excluir o pedido #${orderDisplayCode(order, (storeSettings as any)?.orderCodeStyle)}?`,
      description: "Esta ação não pode ser desfeita.",
      confirmLabel: "Excluir",
    })) {
      deleteOrderMutation.mutate();
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === 'cancelado' && !(await confirm({
      title: "Cancelar este pedido?",
      description: "O pedido será marcado como cancelado.",
      confirmLabel: "Cancelar pedido",
      cancelLabel: "Voltar",
    }))) return;

    if (newStatus === 'enviar_frete') {
      setShowFreightModal(true);
      return;
    }

    if (newStatus === 'avisou_motoboy') {
      const motoboyEntry = storeSettings?.statusMessageTemplates?.["avisou_motoboy"];
      const motoboyEnabled = motoboyEntry ? motoboyEntry.enabled : true;
      if (motoboyEnabled) {
        setShowMotoboyModal(true);
        return;
      }
      updateMutation.mutate({ id: order.id, data: { status: newStatus } });
      return;
    }

    const templates = storeSettings?.statusMessageTemplates;
    const templateEntry = templates?.[newStatus];
    const defaultTemplate = STATUS_MESSAGE_DEFAULTS[newStatus];

    const isEnabled = templateEntry ? templateEntry.enabled : (defaultTemplate ? true : false);

    if (isEnabled && (templateEntry?.template || defaultTemplate)) {
      // Robô de WhatsApp ligado: quem avisa o cliente é o servidor, ao trocar o status.
      if (botSendsStatusTo(whatsappBot, order.customer?.whatsappNumber)) {
        updateMutation.mutate({ id: order.id, data: { status: newStatus } });
        return;
      }
      const rawTemplate = (templateEntry?.template === ENVIADO_ANTIGO ? null : templateEntry?.template) || defaultTemplate;
      // {pontos}: prévia do que o pedido rende ao virar Entregue (mesma conta do servidor, com os bônus).
      let pontos: string | undefined;
      if (newStatus === "entregue" && rawTemplate.includes("{pontos}") && (storeSettings as any)?.loyaltyEnabled) {
        const pv = await customFetch<{ earned: number; balanceAfter: number | null }>(`/api/loyalty/orders/${order.id}/preview`).catch(() => null);
        if (pv && pv.earned > 0) {
          pontos = `Você ganhou ${fmtPontos(pv.earned)}.${pv.balanceAfter != null ? ` Saldo: ${fmtPontos(pv.balanceAfter)}.` : ""}`;
        }
      }
      const message = replaceTemplateVars(rawTemplate, order, storeSettings?.name || "", pontos ? { pontos } : undefined, storeSettings?.dailyOrderNumbering !== false, (storeSettings as any)?.orderCodeStyle);
      setModalState({ status: newStatus, message });
      return;
    }

    updateMutation.mutate({ id: order.id, data: { status: newStatus } });
  };

  const handleFreightSubmit = ({ freight, updatedTotal, message, sendWhatsApp }: { freight: number; updatedTotal: number; productsTotal: number; message: string; sendWhatsApp: boolean }) => {
    const robo = botSendsStatusTo(whatsappBot, order.customer?.whatsappNumber);
    if (sendWhatsApp && !robo) {
      // Sem número de verdade (cliente do PDV), abre sem destinatário com a mensagem pronta (💡 #87).
      abreWhatsApp(linkDoWhatsApp(telefoneDoLink(order.customer?.whatsappNumber), message));
    }
    updateMutation.mutate({
      id: order.id,
      data: {
        status: "enviar_frete",
        deliveryFee: String(freight.toFixed(2)),
        total: String(updatedTotal.toFixed(2)),
        // Só o robô manda (e só se marcado): o envio à mão já saiu acima.
        notifyCustomer: robo && sendWhatsApp,
      },
    });
  };

  const handleSendAndChange = () => {
    if (!modalState) return;
    abreWhatsApp(linkDoWhatsApp(telefoneDoLink(order.customer?.whatsappNumber), modalState.message));
    updateMutation.mutate({ id: order.id, data: { status: modalState.status, notifyCustomer: false } });
  };

  const handleChangeOnly = () => {
    if (!modalState) return;
    updateMutation.mutate({ id: order.id, data: { status: modalState.status, notifyCustomer: false } });
  };

  const styles = statusStyles(order.status);
  const isCancelado = order.status === 'cancelado';
  const statusLabel = ALL_ORDER_STATUSES.find(s => s.id === order.status)?.label ?? order.status;

  return (
    <>
      <Card
        className={`min-w-0 flex flex-col shadow-sm hover:shadow-md transition-shadow group border-l-4 ${styles.border} ${styles.bg} ${isCancelado ? 'opacity-75' : ''}`}
        data-testid={`order-card-${order.status}`}
      >
        <div className="p-4 border-b border-border/50 bg-muted/10">
          <div className="flex justify-between items-start gap-2 mb-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
                <h3 className="font-bold text-lg">#{orderDisplayCode(order as any, (storeSettings as any)?.orderCodeStyle)}</h3>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap uppercase tracking-wide ${styles.badge}`}>
                  {statusLabel}
                </span>
                {(order.items ?? []).some((i: any) => i.scheduledDate) && (
                  <span className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded whitespace-nowrap">📅 Encomenda</span>
                )}
                {((order as any).source === "pdv") && (
                  <span className="text-[10px] font-bold text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded whitespace-nowrap" data-testid={`order-badge-pdv-${order.id}`}>PDV</span>
                )}
                {importado && (
                  <span className="text-[10px] font-bold text-slate-700 bg-slate-100 dark:bg-slate-800 dark:text-slate-300 px-1.5 py-0.5 rounded whitespace-nowrap" data-testid={`order-badge-importado-${order.id}`}>Importado</span>
                )}
                {(order as any).tableNumber && (
                  <span className="text-[10px] font-bold text-blue-700 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 rounded whitespace-nowrap" data-testid={`order-badge-mesa-${order.id}`}>🍽️ {NomeDaMesa((storeSettings as any)?.tableQrPerClient)} {(order as any).tableNumber}{(order as any).mesaDoCliente ? ` · Mesa ${(order as any).mesaDoCliente}` : ""}</span>
                )}
                {order.paymentMethod === "Pendente" && (
                  <span className="text-[10px] font-bold text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300 px-1.5 py-0.5 rounded whitespace-nowrap" data-testid={`order-badge-pendente-${order.id}`}>Pgto pendente</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{formatBRDateTimeShort(order.createdAt)}</p>
            </div>
            {/* Ícones em cima e preço embaixo: na mesma linha, número + 3 ícones +
                preço não cabiam no card estreito e o preço vazava pro vizinho. */}
            <div className="shrink-0 flex flex-col items-end gap-1">
              <div className="flex items-center gap-0.5">
              {/* Pedido do link também edita desde 07/09 (cliente liga pra
                  trocar item). Com cupom não: a edição não recalcula cupom. */}
              {((order as any).source === "pdv" || ((order as any).source === "link" && !(order as any).couponCode && !((order as any).loyaltyPointsUsed > 0))) && order.status !== "cancelado" && (
                <button
                  onClick={() => { window.location.href = `${import.meta.env.BASE_URL.replace(/\/$/, "")}/pdv?edit=${order.id}`; }}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                  title="Editar pedido no PDV"
                  data-testid={`order-edit-pdv-${order.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
              {!importado && <button
                onClick={handlePrint}
                disabled={printing}
                className={`relative p-1.5 rounded-lg hover:bg-muted transition-colors cursor-pointer disabled:opacity-50 ${isPrinted ? 'text-green-600' : isInQueue ? 'text-amber-500' : 'text-muted-foreground hover:text-foreground'}`}
                title={isPrinted ? "Impresso" : isInQueue ? "Na fila de impressão" : vaiPelaJanela ? "Imprimir (via navegador)" : "Imprimir cupom"}
              >
                {printing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isPrinted ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <Printer className="w-4 h-4" />
                )}
                {isInQueue && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-500 border border-card" />
                )}
              </button>}
              <button
                onClick={handleDeleteOrder}
                disabled={deleteOrderMutation.isPending}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer disabled:opacity-50"
                title="Excluir pedido"
              >
                {deleteOrderMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
              </div>
              <div className="text-right max-w-[10rem]">
                <p className="font-bold text-primary whitespace-nowrap">{formatCurrency(order.total)}</p>
                <p className="text-xs font-semibold uppercase leading-tight">{rotuloDaModalidade(order.deliveryMode)}{(order as any).pickupMethod ? ` · ${(order as any).pickupMethod}` : ""}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0">
              {order.customer?.firstName?.charAt(0) || 'C'}
            </div>
            <div className="truncate">
              <p className="font-semibold text-sm truncate">{order.customer?.firstName || 'Cliente'}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <MessageCircle className="w-3 h-3" /> {temWhatsApp(order.customer?.whatsappNumber) ? formatWhatsApp(order.customer?.whatsappNumber) : "Sem WhatsApp"}
              </p>
              {order.customer?.birthDate && (() => {
                const prox = birthdayProximity(String(order.customer.birthDate));
                return (
                  <p className={`text-xs flex items-center gap-1 ${prox === 0 ? "text-pink-600 font-bold" : prox != null ? "text-pink-600 font-medium" : "text-muted-foreground"}`}>
                    🎂 {aniversarioParaMostrar(String(order.customer.birthDate))}
                    {prox === 0 ? " — HOJE!" : prox != null && prox > 0 ? ` — em ${prox} dia${prox > 1 ? "s" : ""}` : prox != null ? ` — foi há ${-prox} dia${prox < -1 ? "s" : ""}` : ""}
                  </p>
                );
              })()}
              {(order as { giftRecipient?: string | null }).giftRecipient && (
                <p className="text-xs font-semibold text-foreground flex items-center gap-1 truncate" data-testid={`order-presente-${order.id}`}>
                  🎁 Presente para: {(order as { giftRecipient?: string | null }).giftRecipient}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 flex-1">
          {order.address && (
            <div className="mb-4 text-xs bg-muted/30 p-2 rounded-lg flex gap-2 items-start">
              <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              <p className="text-muted-foreground leading-relaxed">
                {order.address.street}, {order.address.number}
                {order.address.complement ? ` - ${order.address.complement}` : ''} - {order.address.neighborhood}
                {order.address.city ? `, ${order.address.city}` : ''}
                {order.address.zipCode ? <><br />CEP: {order.address.zipCode}</> : null}
                {(() => {
                  const link = mapsLinkFor(order.address);
                  return link ? (
                    <>
                      <br />
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-1 font-medium text-primary hover:underline"
                        data-testid={`order-maps-link-${order.id}`}
                      >
                        Abrir no mapa
                      </a>
                    </>
                  ) : null;
                })()}
              </p>
            </div>
          )}
          <div className="space-y-1 text-sm">
            <p className="font-medium text-muted-foreground mb-2">Resumo dos itens ({order.items?.length || 0})</p>
            {/* Data da encomenda UMA vez quando é a mesma em todos os itens agendados (data única no carrinho,
                05/10/2026); com datas diferentes continua uma por item. */}
            {(() => {
              const dc = dataUnicaDosItens(order.items ?? []);
              return dc ? (
                <p className="text-xs font-semibold text-orange-600 mb-1" data-testid="pedido-data-encomenda">
                  📅 Encomenda para {encomendaPorExtenso(dc.data, dc.hora, dc.turno)}
                </p>
              ) : null;
            })()}
            {(order.items ?? []).flatMap((item: any) => {
              // Com adicional e quantidade > 1, uma linha por unidade
              // ("1 de 2"): senão o lojista não sabe se são dois adicionais num
              // item só ou um em cada — foi o que confundiu o Terraço 29 Burguer
              // (03/08/2026). Sem adicional continua junto ("4x Açaí").
              const n = Math.max(1, Number(item.quantity) || 1);
              return n > 1 && item.selectedOptions?.length
                ? Array.from({ length: n }, (_, k) => ({ ...item, quantity: 1, __unidade: `${k + 1} de ${n}` }))
                : [item];
            }).map((item: any, itemIdx: number) => {
              // Agrupado por GRUPO (como a mensagem do WhatsApp e o cupom):
              // sem o título, os grupos "(Primeiro)/(Segundo)" que o lojista
              // cria pra desambiguar combo somem e vira tudo um bolo (relato
              // Açaí Ronconi, 02/09). Respeita o mesmo flag da loja.
              const grupos = new Map<string, { title: string; opcoes: Map<string, { name: string; price: number; count: number }> }>();
              // «Cada lista conta separado» (28/10/2026): a conta de cada lista vai no título dela, com cada adicional embaixo
              // do seu copo; os títulos ficam mesmo com a loja escondendo os dos grupos (senão não dá para saber o copo).
              const listasDoPacote: Array<{ nome: string }> = (item.prepCount as any)?.listas ?? [];
              const contaDaLista = new Map(gruposComConta([], item.prepCount as any).map((g) => [g.titulo, g.conta]));
              if (item.selectedOptions?.length) {
                item.selectedOptions.forEach((o: any) => {
                  const title = hideGroupTitles && listasDoPacote.length === 0 ? "" : String(o.groupName ?? "").trim();
                  if (!grupos.has(title)) grupos.set(title, { title, opcoes: new Map() });
                  const g = grupos.get(title)!;
                  const key = o.optionName || o.name;
                  if (!g.opcoes.has(key)) g.opcoes.set(key, { name: key, price: Number(o.price) || 0, count: 0 });
                  g.opcoes.get(key)!.count++;
                });
              }
              const discAmt = Number((item as any).discountAmount || 0);
              const origPrice = Number((item as any).originalPrice || 0);
              const qty = Number(item.quantity || 0);
              return (
                <div key={`${item.id}-${itemIdx}`}>
                  <div className="flex justify-between gap-2">
                    <p className="truncate">{item.__unidade ? `${item.productName} (${item.__unidade})` : `${item.quantity}x ${item.productName}`}{item.weightKg != null ? ` (${item.weightUnit === "ml" || item.weightUnit === "g" ? formatarPeso(parseFloat(item.weightKg), item.weightUnit) : `${String(parseFloat(item.weightKg).toFixed(3)).replace(".", ",")} kg`})` : ""}</p>
                    {discAmt > 0 && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-accent-foreground bg-accent px-1.5 py-0.5 rounded shrink-0">Promo</span>
                    )}
                  </div>
                  {discAmt > 0 && origPrice > 0 && (
                    <p className="text-xs text-muted-foreground ml-4">
                      <span className="line-through">{formatCurrency(origPrice * qty)}</span>{" "}
                      <span className="text-accent font-semibold">por {formatCurrency(Math.max(0, (origPrice - discAmt)) * qty)}</span>
                    </p>
                  )}
                  {/* Pacote de escolhas: o retrato gravado no item ("Preparos: 8½ (7 inclusos + 2 extras)"). */}
                  {item.prepCount && (listasDoPacote.length > 0
                    ? prepCountLines({ ...(item.prepCount as any), listas: listasDoPacote.filter((l) => !grupos.has(l.nome)) }).filter(() => listasDoPacote.some((l) => !grupos.has(l.nome)))
                    : prepCountLines(item.prepCount)
                  ).map((l, li) => <p key={li} className="text-xs font-medium text-foreground/80 ml-4" data-testid="order-prep-line">{l}</p>)}
                  {Array.from(grupos.values()).map((grp, gi) => (
                    <div key={gi}>
                      {grp.title && (
                        <p className="text-xs font-semibold text-foreground/80 ml-4 mt-0.5" data-testid={contaDaLista.get(grp.title) ? "order-prep-line" : undefined}>
                          {grp.title}:{contaDaLista.get(grp.title) ? ` ${contaDaLista.get(grp.title)}` : ""}
                        </p>
                      )}
                      {Array.from(grp.opcoes.values()).map((g, i) => (
                        <p key={i} className="text-xs text-muted-foreground ml-4">+ {g.count > 1 ? `${g.count}x ` : ''}{g.name}{g.price > 0 ? ` ${formatCurrency(g.price * g.count)}` : ''}</p>
                      ))}
                    </div>
                  ))}
                  {item.removedIngredients?.length > 0 && (
                    <p className="text-xs text-destructive/70 ml-4">Retirar: {item.removedIngredients.join(', ')}</p>
                  )}
                  {item.scheduledDate && !dataUnicaDosItens(order.items ?? []) && (
                    <p className="text-xs text-orange-600 ml-4">📅 {encomendaPorExtenso(item.scheduledDate, item.scheduledTime, item.scheduledShift)}</p>
                  )}
                </div>
              );
            })}
          </div>
          {order.notes && (
            <div className="mt-3 text-xs p-2 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded border border-yellow-200 dark:border-yellow-900/50">
              <strong>Obs:</strong> {order.notes}
            </div>
          )}
          <div className="mt-3 text-xs p-2 bg-muted/40 rounded border border-border/50 space-y-0.5">
            <p className="flex items-center gap-1.5">
              <span><strong>Pagamento:</strong> {order.paymentMethod || "Não informado"}</span>
              {order.paymentMethod !== "Pendente" && order.status !== "cancelado" && !importado && (
                <button
                  type="button"
                  onClick={() => setEditingPayment(order)}
                  className="p-0.5 rounded text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Alterar forma de pagamento"
                  data-testid={`order-edit-payment-${order.id}`}
                >
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </p>
            {Array.isArray((order as any).paymentParts) && (order as any).paymentParts.length > 0 && (
              (order as any).paymentParts.map((pp: { method: string; amount: number }, i: number) => (
                <p key={i} className="pl-2">{pp.method}: {formatCurrency(Number(pp.amount) || 0)}</p>
              ))
            )}
            {Array.isArray((order as any).loyaltyRewards) && (order as any).loyaltyRewards.length > 0 && (
              <p className="text-green-700 dark:text-green-400" data-testid={`order-pontos-${order.id}`}>
                <strong>Pontos:</strong>{" "}
                {((order as any).loyaltyRewards as Array<{ name: string; points: number; discount: number }>)
                  .map((r) => `${r.name} (${r.points} pts)${r.discount > 0 ? ` −${formatCurrency(r.discount)}` : ""}`)
                  .join(" · ")}
              </p>
            )}
            {(() => {
              // Mesma conta das mensagens de Enviar frete e Avisou Motoboy.
              const troco = trocoDoPedido(order as any, Number(order.total || 0));
              if (!troco) return null;
              return (
                <>
                  <p><strong>Troco para:</strong> {formatCurrency(troco.para)}</p>
                  {troco.devolver > 0 && (
                    <p className="text-green-700 dark:text-green-400 font-semibold">
                      Troco a devolver: {formatCurrency(troco.devolver)}
                    </p>
                  )}
                </>
              );
            })()}
            {(() => {
              const surcharge = Number(order.cardSurcharge || 0);
              const taxaServico = Number((order as any).taxaServico || 0);
              const pctServico = Number((order as any).taxaServicoPct || 0);
              const fee = Number(order.deliveryFee || 0);
              const total = Number(order.total || 0);
              const subtotal = total - fee - surcharge - taxaServico;
              if (surcharge <= 0 && taxaServico <= 0) return null;
              return (
                <div className="mt-2 pt-2 border-t border-border/50 space-y-0.5">
                  <p className="flex justify-between"><span>Subtotal:</span> <span>{formatCurrency(subtotal)}</span></p>
                  {fee > 0 && (
                    <p className="flex justify-between"><span>Taxa de entrega:</span> <span>{formatCurrency(fee)}</span></p>
                  )}
                  {surcharge > 0 && (
                    <p className="flex justify-between"><span>Acréscimo ({order.paymentMethod}):</span> <span>{formatCurrency(surcharge)}</span></p>
                  )}
                  {taxaServico > 0 && (
                    <p className="flex justify-between" data-testid={`order-taxa-servico-${order.id}`}>
                      <span>Taxa de serviço{pctServico > 0 ? ` (${String(pctServico).replace(".", ",")}%)` : ""}:</span> <span>{formatCurrency(taxaServico)}</span>
                    </p>
                  )}
                  <p className="flex justify-between font-semibold"><span>Total:</span> <span>{formatCurrency(total)}</span></p>
                </div>
              );
            })()}
            <OrderHistory orderId={order.id} />
          </div>
        </div>

        {importado ? (
          <div className="p-3 bg-muted/5 border-t border-border/50">
            <p className="text-xs text-muted-foreground" data-testid={`order-importado-nota-${order.id}`}>Importado de outro cardápio: é só histórico e não muda.</p>
          </div>
        ) : (
          <div className="p-3 bg-muted/5 border-t border-border/50 space-y-1.5">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Alterar Status</p>
            <StatusDropdown
              currentStatus={order.status}
              onSelect={handleStatusChange}
              isPending={updateMutation.isPending}
              hideStatuses={supportsFreightStep ? [] : ['enviar_frete']}
            />
          </div>
        )}
      </Card>

      {showFreightModal && (
        <SendFreightModal
          order={order}
          storeSettings={storeSettings}
          onClose={() => setShowFreightModal(false)}
          onSubmit={handleFreightSubmit}
          isPending={updateMutation.isPending}
        />
      )}

      {modalState && (
        <WhatsAppMessageModal
          order={order}
          newStatus={modalState.status}
          message={modalState.message}
          onMessageChange={(msg) => setModalState({ ...modalState, message: msg })}
          onSendAndChange={handleSendAndChange}
          onChangeOnly={handleChangeOnly}
          onClose={() => setModalState(null)}
          isPending={updateMutation.isPending}
        />
      )}

      {showMotoboyModal && (
        <MotoboyMessageModal
          order={order}
          onStatusChanged={() => setShowMotoboyModal(false)}
          onClose={() => setShowMotoboyModal(false)}
          isPending={updateMutation.isPending}
          updateMutation={updateMutation}
        />
      )}
      {editingPayment && (
        <PaymentMethodModal order={editingPayment} onClose={() => setEditingPayment(null)} />
      )}
    </>
  );
}

// Histórico do pedido (09/09/2026): edição de itens, forma de pagamento e
// status — quem, quando, antes e depois. Carrega só quando o lojista abre.
function OrderHistory({ orderId }: { orderId: string }) {
  const [open, setOpen] = useState(false);
  const { data: events = [], isLoading } = useQuery<any[]>({
    queryKey: ["order-events", orderId],
    queryFn: () => customFetch(`/api/orders/${orderId}/events`),
    enabled: open,
    staleTime: 30_000,
  });
  const statusLabel = (s: string) => ALL_ORDER_STATUSES.find((x) => x.id === s)?.label || s || "—";
  const itens = (list: any[]) =>
    (list || []).map((i) => `${i.qtd}x ${i.nome}${i.opcoes?.length ? ` (${i.opcoes.join(", ")})` : ""}`).join(", ") || "—";
  // Edição de cliente, modalidade, endereço e taxa (kind "dados"): o evento traz só o que mudou.
  const mudou = (ev: any, campo: string) => campo in (ev.before ?? {}) || campo in (ev.after ?? {});
  const nomeCliente = (c: any) => c?.nome || "Cliente avulso";
  // Mesmo nome dos dois lados: o número diz qual é qual.
  const cliente = (c: any, outro: any) =>
    c?.whatsapp && nomeCliente(c) === nomeCliente(outro) ? `${nomeCliente(c)} (${formatWhatsApp(c.whatsapp)})` : nomeCliente(c);
  const modalidade = (m: any) => rotuloDaModalidade(m);
  return (
    <div className="mt-2 pt-2 border-t border-border/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
        aria-expanded={open}
        data-testid={`order-history-toggle-${orderId}`}
      >
        <Clock className="w-3 h-3" /> Histórico
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1.5 space-y-1.5" data-testid={`order-history-${orderId}`}>
          {isLoading ? (
            <p className="text-muted-foreground">Carregando…</p>
          ) : events.length === 0 ? (
            <p className="text-muted-foreground">Nenhuma alteração registrada.</p>
          ) : (
            events.map((ev) => (
              <div key={ev.id}>
                <p className="text-muted-foreground">
                  {formatBRDateTimeShort(ev.createdAt)}{ev.userName ? ` · ${ev.userName}` : ""}
                </p>
                {ev.kind === "status" && (
                  <p>
                    Status: {statusLabel(ev.before?.status)} → {statusLabel(ev.after?.status)}
                    {ev.after?.frete != null ? ` · frete ${formatCurrency(Number(ev.after.frete))}` : ""}
                  </p>
                )}
                {ev.kind === "pagamento" && (
                  <p>
                    Pagamento: {ev.before?.pagamento || "—"} → {ev.after?.pagamento}
                    {Number(ev.after?.troco) > 0 ? ` (troco para ${formatCurrency(Number(ev.after.troco))})` : ""}
                  </p>
                )}
                {ev.kind === "itens" && (
                  <>
                    <p>Antes: {itens(ev.before?.itens)} · {formatCurrency(Number(ev.before?.total) || 0)}</p>
                    <p>Depois: {itens(ev.after?.itens)} · {formatCurrency(Number(ev.after?.total) || 0)}</p>
                  </>
                )}
                {ev.kind === "dados" && (
                  <>
                    {mudou(ev, "cliente") && <p>Cliente: {cliente(ev.before?.cliente, ev.after?.cliente)} → {cliente(ev.after?.cliente, ev.before?.cliente)}</p>}
                    {mudou(ev, "modalidade") && <p>Modalidade: {modalidade(ev.before?.modalidade)} → {modalidade(ev.after?.modalidade)}</p>}
                    {mudou(ev, "endereco") && <p>Endereço: {ev.before?.endereco || "—"} → {ev.after?.endereco || "—"}</p>}
                    {mudou(ev, "taxa") && <p>Taxa de entrega: {formatCurrency(Number(ev.before?.taxa) || 0)} → {formatCurrency(Number(ev.after?.taxa) || 0)}</p>}
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Corrige a forma de pagamento RECEBIDA (pediu Dinheiro no link, pagou PIX na
// retirada). Total não muda — o Caixa lê a forma do pedido e se corrige.
function PaymentMethodModal({ order, onClose }: { order: any; onClose: () => void }) {
  const { data: storeSettings } = useStoreSettings();
  const [method, setMethod] = useState<string>(order.paymentMethod || "Dinheiro");
  // Troco: "vai precisar de troco?" + "troco para quanto". Faltava aqui —
  // pedido que pediu Pix e virou Dinheiro saía sem troco (relato de 07/09).
  const currentChangeFor = Number(order.changeFor) > 0 ? Number(order.changeFor) : 0;
  const [needsChange, setNeedsChange] = useState(currentChangeFor > 0);
  const [changeForStr, setChangeForStr] = useState(currentChangeFor > 0 ? currentChangeFor.toFixed(2).replace(".", ",") : "");
  const changeForNum = needsChange ? (parseFloat(changeForStr.replace(/\./g, "").replace(",", ".")) || 0) : 0;
  const dirty = method !== order.paymentMethod || (method === "Dinheiro" && changeForNum !== currentChangeFor);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const methods: string[] = [
    ...(((storeSettings as any)?.acceptedPayments ?? ["Dinheiro", "Pix", "Débito", "Crédito"]) as string[]),
    ...((((storeSettings as any)?.crediarioMethods ?? []) as Array<{ name: string }>).map((m) => m.name)),
  ];
  const options = Array.from(new Set([...(order.paymentMethod ? [order.paymentMethod] : []), ...methods]));

  const handleSave = async () => {
    setSaving(true);
    try {
      await customFetch(`/api/orders/${order.id}/payment-method`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentMethod: method,
          ...(method === "Dinheiro" ? { changeFor: changeForNum > 0 ? changeForNum : null } : {}),
        }),
      });
      queryClient.invalidateQueries({ queryKey: getListOrdersQueryKey() });
      queryClient.invalidateQueries({ queryKey: ["cash-current"] });
      toast({ title: "Forma de pagamento atualizada!" });
      onClose();
    } catch (err: any) {
      toast({
        title: errorTitle("alterar a forma de pagamento"),
        description: extractApiErrorMessage(err, TRY_AGAIN_DESCRIPTION),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Forma de pagamento"
      subtitle="Como o cliente pagou de verdade. O total do pedido não muda."
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || !dirty} data-testid="order-payment-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Salvar
          </Button>
        </>
      }
    >
      <select
        className="w-full border rounded-md p-2 text-sm bg-background h-10"
        value={method}
        onChange={(e) => setMethod(e.target.value)}
        data-testid="order-payment-select"
      >
        {options.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      {method === "Dinheiro" && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="order-needs-change" className="text-sm font-normal">Vai precisar de troco?</Label>
            <Switch id="order-needs-change" checked={needsChange} onCheckedChange={setNeedsChange} data-testid="order-payment-needs-change" />
          </div>
          {needsChange && (
            <Input
              inputMode="decimal"
              placeholder={`Troco para quanto? Total ${formatCurrency(Number(order.total) || 0)}`}
              value={changeForStr}
              onChange={(e) => setChangeForStr(e.target.value)}
              data-testid="order-payment-change-for"
            />
          )}
        </div>
      )}
    </ModalShell>
  );
}
